import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  FileText, Clock, CheckCircle, XCircle, CurrencyInr,
  ArrowRight, ChartLine, CalendarBlank, FunnelSimple, MagnifyingGlass,
  TrendUp, TrendDown, Users, Heartbeat, ShieldCheck,
  ArrowsClockwise, WarningCircle, Wallet, Receipt, Scales,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';

const CHART_COLORS = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B', '#F43F5E', '#06B6D4', '#F97316', '#EC4899'];
const STATUS_COLORS = {
  registered: '#6366F1', eligibility_pending: '#F59E0B', preauth_pending: '#F59E0B',
  preauth_approved: '#10B981', partial_approved: '#D97706', documents_pending: '#F97316',
  submitted: '#3B82F6', query_raised: '#EAB308', denied: '#EF4444',
  under_appeal: '#F97316', payment_pending: '#8B5CF6', partially_settled: '#06B6D4',
  settled: '#10B981', closed: '#64748B', written_off: '#94A3B8',
};

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
const formatCompact = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toString();
};
const formatStatus = (s) => s?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [recentClaims, setRecentClaims] = useState([]);
  const [payerStats, setPayerStats] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async (fromDate, toDate) => {
    try {
      const params = {};
      if (fromDate || dateFrom) params.date_from = fromDate || dateFrom;
      if (toDate || dateTo) params.date_to = toDate || dateTo;
      const [statsRes, statusRes, recentRes, payerRes, trendsRes] = await Promise.all([
        dashboardApi.stats(params), dashboardApi.statusCounts(params),
        dashboardApi.recentClaims(5), dashboardApi.payerStats(), dashboardApi.claimTrends(14),
      ]);
      setStats(statsRes.data);
      setStatusCounts(statusRes.data);
      setRecentClaims(recentRes.data);
      setPayerStats(payerRes.data);
      setTrends(trendsRes.data);
    } catch (e) { console.error('Dashboard load failed:', e); }
    finally { setLoading(false); }
  };

  const handleDateFilter = () => { loadDashboardData(dateFrom, dateTo); };
  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); loadDashboardData('', ''); };

  const statusData = Object.entries(statusCounts)
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({ name: formatStatus(status), value: count, color: STATUS_COLORS[status] || '#64748B' }));

  const collectionRate = stats?.total_billed > 0 ? Math.round((stats?.total_paid / stats?.total_billed) * 100) : 0;
  const approvalRate = stats?.total_billed > 0 ? Math.round((stats?.total_approved / stats?.total_billed) * 100) : 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Header — Hospital Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 rounded-2xl p-6 text-white" data-testid="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {user?.tenant_name && (
              <div className="h-14 w-14 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <span className="text-xl font-bold text-white font-['Outfit']">{user.tenant_name.charAt(0)}</span>
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="dashboard-heading">{user?.tenant_name || 'Dashboard'}</h1>
              <p className="text-sm text-slate-300 mt-0.5">Welcome back, {user?.name} &middot; Revenue Cycle Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <p className="text-2xl font-bold font-['Outfit']">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{new Date().toLocaleDateString('en-IN', { weekday: 'long' })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="dashboard-date-filter">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="h-9 w-40 text-sm border-slate-300" data-testid="dashboard-date-from" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="h-9 w-40 text-sm border-slate-300" data-testid="dashboard-date-to" />
        </div>
        <Button size="sm" onClick={handleDateFilter} className="h-9 bg-sky-600 hover:bg-sky-700 text-white text-xs gap-1.5" data-testid="dashboard-filter-btn">
          <MagnifyingGlass className="h-3.5 w-3.5" /> Search
        </Button>
        {(dateFrom || dateTo) && (
          <Button size="sm" variant="outline" onClick={clearDateFilter} className="h-9 text-xs gap-1.5" data-testid="dashboard-clear-filter-btn">
            <ArrowsClockwise className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      {/* Top KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Cases', value: stats?.total_claims || 0, icon: FileText, iconColor: 'text-sky-500', bg: 'bg-sky-50', border: 'border-sky-200', link: '/claims' },
          { label: 'Pending', value: stats?.pending_claims || 0, icon: Clock, iconColor: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', link: '/claims?status=registered' },
          { label: 'Approved', value: stats?.approved_claims || 0, icon: CheckCircle, iconColor: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200', link: '/claims?status=preauth_approved' },
          { label: 'Denied', value: stats?.denied_claims || 0, icon: XCircle, iconColor: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', link: '/claims?status=denied' },
        ].map((c, i) => (
          <Card key={i} className={`${c.bg} border ${c.border} cursor-pointer hover:shadow-md transition-all`}
            onClick={() => navigate(c.link)} data-testid={`stat-card-${i}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`h-11 w-11 rounded-xl bg-white/80 flex items-center justify-center shadow-sm`}>
                <c.icon className={`h-5 w-5 ${c.iconColor}`} weight="duotone" />
              </div>
              <div>
                <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">{c.label}</p>
                <p className="text-2xl font-bold text-slate-800 font-['Outfit'] leading-tight">{c.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Financial Summary */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { label: 'Total Billed', value: stats?.total_billed, icon: Receipt, accent: 'text-sky-600' },
              { label: 'Approved', value: stats?.total_approved, icon: ShieldCheck, accent: 'text-violet-600' },
              { label: 'Collected', value: stats?.total_paid, icon: Wallet, accent: 'text-emerald-600' },
              { label: 'Outstanding', value: stats?.total_outstanding, icon: WarningCircle, accent: 'text-rose-600' },
              { label: 'Deductions', value: (stats?.total_billed || 0) - (stats?.total_approved || 0), icon: Scales, accent: 'text-amber-600' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <f.icon className={`h-7 w-7 ${f.accent} shrink-0 opacity-80`} weight="duotone" />
                <div>
                  <p className="text-[9px] text-slate-400 uppercase tracking-wider font-medium">{f.label}</p>
                  <p className={`text-lg font-bold font-['Outfit'] ${f.accent}`}>{formatCurrency(f.value)}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Progress bars */}
          <div className="mt-4 flex items-center gap-3">
            <span className="text-[9px] text-slate-400 uppercase tracking-wider w-20">Collection</span>
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all" style={{ width: `${Math.min(collectionRate, 100)}%` }} />
            </div>
            <span className="text-xs font-bold text-emerald-600 w-12 text-right">{collectionRate}%</span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-[9px] text-slate-400 uppercase tracking-wider w-20">Approval</span>
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 to-violet-400 rounded-full transition-all" style={{ width: `${Math.min(approvalRate, 100)}%` }} />
            </div>
            <span className="text-xs font-bold text-violet-600 w-12 text-right">{approvalRate}%</span>
          </div>
        </CardContent>
      </Card>

      {/* KPI Pills */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Denial Rate', value: `${stats?.denial_rate || 0}%`, color: stats?.denial_rate > 10 ? 'text-red-600' : 'text-emerald-600', bg: stats?.denial_rate > 10 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200', icon: stats?.denial_rate > 10 ? TrendUp : TrendDown },
          { label: 'Avg Processing', value: `${stats?.avg_processing_days || 0} days`, color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200', icon: Clock },
          { label: 'Collection Rate', value: `${collectionRate}%`, color: collectionRate > 50 ? 'text-emerald-600' : 'text-amber-600', bg: collectionRate > 50 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200', icon: CurrencyInr },
          { label: 'Active Cases', value: (stats?.total_claims || 0) - (stats?.denied_claims || 0), color: 'text-sky-600', bg: 'bg-sky-50 border-sky-200', icon: Heartbeat },
        ].map((k, i) => (
          <div key={i} className={`${k.bg} border rounded-xl p-3 flex items-center gap-3`}>
            <k.icon className={`h-5 w-5 ${k.color} shrink-0`} weight="bold" />
            <div>
              <p className="text-[9px] text-slate-500 uppercase tracking-wider font-medium">{k.label}</p>
              <p className={`text-lg font-bold ${k.color} font-['Outfit']`}>{k.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Trend Chart — 2 cols */}
        <Card className="bg-white border border-slate-200 shadow-sm lg:col-span-2" data-testid="claim-trends-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <ChartLine className="h-4 w-4 text-sky-500" /> Case Trends (14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends}>
                  <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }}
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                  <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px', color: '#fff', fontSize: 12 }}
                    labelFormatter={(v) => new Date(v).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })} />
                  <Area type="monotone" dataKey="count" stroke="#0EA5E9" strokeWidth={2} fill="url(#trendFill)" name="Cases" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Pie — 1 col */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="status-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">By Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={35} outerRadius={65} paddingAngle={3} strokeWidth={0}>
                    {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px', color: '#fff', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {statusData.map((s, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-[9px] text-slate-500">{s.name} ({s.value})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Cases */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="recent-claims-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Recent Cases</CardTitle>
            <Button variant="ghost" size="sm" className="text-sky-600 hover:bg-sky-50 h-7 text-xs gap-1" onClick={() => navigate('/claims')}>
              View All <ArrowRight className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentClaims.length === 0 ? (
              <p className="text-slate-400 text-center py-8 text-sm">No cases yet</p>
            ) : (
              <div className="space-y-2">
                {recentClaims.map((c) => (
                  <div key={c.id} onClick={() => navigate(`/claims/${c.id}`)}
                    className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors border border-transparent hover:border-slate-200 group">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center text-[10px] font-bold text-sky-700">
                        {c.claim_number?.slice(-3)}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{c.claim_number}</p>
                        <p className="text-[10px] text-slate-400">{c.patient_name}</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className="text-xs font-semibold text-slate-700">{formatCurrency(c.billed_amount)}</p>
                        <Badge className="text-[8px] border-0 mt-0.5" style={{ backgroundColor: `${STATUS_COLORS[c.status]}20`, color: STATUS_COLORS[c.status] }}>
                          {formatStatus(c.status)}
                        </Badge>
                      </div>
                      <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-sky-500 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payer Performance */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payer-stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Top Payers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={payerStats} layout="vertical" barSize={16}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                  <YAxis type="category" dataKey="payer" tick={{ fontSize: 10, fill: '#64748B' }} width={90} />
                  <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px', color: '#fff', fontSize: 12 }} />
                  <Bar dataKey="total_claims" fill="#8B5CF6" name="Cases" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
